import React from "react";

import {
  normalizeRoleName,
  ROLE_DISPLAY_NAMES,
  RoleType,
} from "../../Utils/Constants/RoleConstants";
import { useAccountTiers } from "../../Utils/Helper/accountTierHelpers";
import { formatDateTime } from "../../Utils/Helper/Helper";

import "./style.scss";

interface PreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  recordCount: string;
  uploadType: string;
  data: any[];
}

type ColumnConfig = { label: string; keys: string[] };

const EXECUTIVES_COLUMNS: ColumnConfig[] = [
  { label: "NAME", keys: ["name", "full_name"] },
  { label: "TITLE", keys: ["title", "position"] },
  { label: "EMAIL", keys: ["email"] },
  { label: "PHONE", keys: ["phone"] },
  { label: "CITY", keys: ["city"] },
  { label: "COUNTRY", keys: ["country"] },
  { label: "REGION", keys: ["region"] },
  { label: "SPECIALIZATION", keys: ["specialization"] },
  { label: "GRADE", keys: ["grade"] },
  { label: "LANGUAGES", keys: ["languages"] },
  { label: "MAX ACCOUNTS", keys: ["max_accounts"] },
  { label: "CERTIFICATIONS", keys: ["certifications"] },
  { label: "ROLE", keys: ["role"] },
];

const ACCOUNTS_COLUMNS: ColumnConfig[] = [
  { label: "NAME", keys: ["name"] },
  { label: "INDUSTRY", keys: ["industry", "industry_vertical"] },
  { label: "TIER", keys: ["tier"] },
  { label: "CHURN RISK", keys: ["churn_risk"] },
  { label: "REVENUE", keys: ["revenue"] },
  { label: "EMPLOYEES", keys: ["employees"] },
  { label: "CITY", keys: ["city"] },
  { label: "COUNTRY", keys: ["country"] },
  { label: "CONTACT PERSON", keys: ["contact_person"] },
  { label: "CONTACT EMAIL", keys: ["contact_email", "email"] },
  { label: "CONTACT PHONE", keys: ["contact_phone", "phone"] },
  { label: "RENEWAL DATE", keys: ["renewal_date"] },
];

const COMPOSITE_COLUMNS: ColumnConfig[] = [
  { label: "TITLE", keys: ["executive_title", "title"] },
  { label: "EMAIL", keys: ["executive_email", "email"] },
  { label: "PHONE", keys: ["executive_phone", "phone"] },
  { label: "REGION", keys: ["executive_region", "region"] },
  {
    label: "SPECIALIZATION",
    keys: ["executive_specialization", "specialization"],
  },
  { label: "GRADE", keys: ["executive_grade", "grade"] },
  { label: "LANGUAGES", keys: ["executive_languages", "languages"] },
  { label: "ACCOUNT NAME", keys: ["account_name"] },
  { label: "INDUSTRY", keys: ["account_industry", "industry"] },
  { label: "TIER", keys: ["account_tier", "tier"] },
  { label: "CHURN RISK", keys: ["account_churn_risk", "churn_risk"] },
  { label: "REVENUE", keys: ["account_revenue", "revenue"] },
  { label: "EMPLOYEES", keys: ["account_employees", "employees"] },
  { label: "ACCOUNT CITY", keys: ["account_city", "city"] },
  { label: "ACCOUNT COUNTRY", keys: ["account_country", "country"] },
  {
    label: "CONTACT PERSON",
    keys: ["account_contact_person", "contact_person"],
  },
  { label: "CONTACT EMAIL", keys: ["account_contact_email", "contact_email"] },
  { label: "CONTACT PHONE", keys: ["account_contact_phone", "contact_phone"] },
  { label: "RENEWAL DATE", keys: ["account_renewal_date", "renewal_date"] },
  { label: "RELATIONSHIP TYPE", keys: ["relationship_type", "type"] },
];

const ACCOUNT_TEAM_COLUMNS: ColumnConfig[] = [
  { label: "ACCOUNT", keys: ["account_name", "account"] },
  { label: "MEMBER NAME", keys: ["member_name", "name", "full_name"] },
  { label: "MEMBER TITLE", keys: ["member_title", "title", "position"] },
  { label: "MEMBER EMAIL", keys: ["member_email", "email"] },
  { label: "MEMBER PHONE", keys: ["member_phone", "phone"] },
  { label: "MEMBER LANGUAGES", keys: ["member_languages", "languages"] },
  { label: "ACCOUNT TEAM ROLE", keys: ["role"] },
];

const CUSTOMER_TEAM_COLUMNS: ColumnConfig[] = [
  { label: "ACCOUNT", keys: ["account_name", "account"] },
  { label: "MEMBER NAME", keys: ["member_name", "name", "full_name"] },
  { label: "MEMBER TITLE", keys: ["member_title", "title", "position"] },
  { label: "MEMBER EMAIL", keys: ["member_email", "email"] },
  { label: "MEMBER PHONE", keys: ["member_phone", "phone"] },
  { label: "MEMBER LANGUAGES", keys: ["member_languages", "languages"] },
  { label: "CUSTOMER TEAM ROLE", keys: ["role"] },
];

const UPLOAD_TYPE_COLUMNS: Record<string, ColumnConfig[]> = {
  users: EXECUTIVES_COLUMNS,
  executives: EXECUTIVES_COLUMNS,
  customers: ACCOUNTS_COLUMNS,
  accounts: ACCOUNTS_COLUMNS,
  add_customer: ACCOUNTS_COLUMNS,
  customer_update: ACCOUNTS_COLUMNS,
  add_user: EXECUTIVES_COLUMNS,
  composite: COMPOSITE_COLUMNS,
  user_update: EXECUTIVES_COLUMNS,
  delete_user: EXECUTIVES_COLUMNS,
  delete_customer: ACCOUNTS_COLUMNS,
  upload_account_team_members: ACCOUNT_TEAM_COLUMNS,
  upload_customer_team_members: CUSTOMER_TEAM_COLUMNS,
  add_account_team_members: ACCOUNT_TEAM_COLUMNS,
  add_customer_team_members: CUSTOMER_TEAM_COLUMNS,
  update_account_team_members: ACCOUNT_TEAM_COLUMNS,
  update_customer_team_members: CUSTOMER_TEAM_COLUMNS,
};

const EXCLUDED_KEYS = new Set([
  "id",
  "customer_id",
  "user_id",
  "account_id",
  "created_at",
  "updated_at",
  "account_created_at",
  "account_updated_at",
]);

const DATE_KEYS = new Set([
  "created_at",
  "updated_at",
  "uploaded_at",
  "date",
  "timestamp",
  "last_login_at",
  "renewal_date",
]);

const ISO_DATE_REGEX =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})?$/;

function getColumnsForUploadType(uploadType: string): ColumnConfig[] | null {
  return UPLOAD_TYPE_COLUMNS[uploadType] ?? null;
}

function getFallbackHeaders(
  firstRecord: Record<string, unknown>
): ColumnConfig[] {
  return Object.keys(firstRecord)
    .filter(k => {
      if (EXCLUDED_KEYS.has(k.toLowerCase())) return false;
      const v = firstRecord[k];
      return typeof v !== "object" || v === null || Array.isArray(v);
    })
    .map(k => ({ label: k.replace(/_/g, " ").toUpperCase(), keys: [k] }));
}

function isDateValue(value: unknown, key?: string): boolean {
  if (typeof value !== "string") return false;
  if (key && DATE_KEYS.has(key.toLowerCase())) return true;
  if (ISO_DATE_REGEX.test(value)) return true;
  return !isNaN(new Date(value).getTime()) && value.length > 10;
}

function formatRoleDisplayValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "-";
  const normalizedRole = normalizeRoleName(String(value));
  if (normalizedRole && normalizedRole in ROLE_DISPLAY_NAMES) {
    return ROLE_DISPLAY_NAMES[normalizedRole as RoleType];
  }
  return String(value);
}

function isRoleColumn(col: ColumnConfig): boolean {
  return col.keys.some(key => key.toLowerCase() === "role");
}

function isTierColumn(col: ColumnConfig): boolean {
  return col.keys.some(key => {
    const normalized = key.toLowerCase();
    return normalized === "tier" || normalized === "account_tier";
  });
}

function formatCellValue(value: unknown, key?: string): string {
  if (value === null || value === undefined) return "-";
  if (typeof value === "object")
    return Array.isArray(value) ? value.join(", ") : JSON.stringify(value);
  if (isDateValue(value, key)) {
    try {
      return formatDateTime(value as string).dateTime;
    } catch {
      return String(value);
    }
  }
  return String(value);
}

function getRecordValue(record: Record<string, any>, key: string): unknown {
  const v = record[key];
  if (v !== undefined && v !== null && v !== "") return v;
  const profile = record?.executiveProfile;
  const pv = profile?.[key];
  if (pv !== undefined && pv !== null && pv !== "") return pv;
  return undefined;
}

function getCellValue(
  record: Record<string, any>,
  col: ColumnConfig,
  formatTier?: (code: string | null | undefined) => string
): string {
  for (const key of col.keys) {
    const value = getRecordValue(record, key);
    if (value !== undefined && value !== null && value !== "") {
      if (isRoleColumn(col)) {
        return formatRoleDisplayValue(value);
      }
      if (isTierColumn(col) && formatTier) {
        return formatTier(String(value)) || String(value);
      }
      return formatCellValue(value, key);
    }
  }
  return "-";
}

function truncate(str: string, max = 80): string {
  return str.length > max ? `${str.substring(0, max)}...` : str;
}

const PreviewModal: React.FC<PreviewModalProps> = ({
  isOpen,
  onClose,
  title,
  recordCount,
  uploadType,
  data,
}) => {
  const { formatTier } = useAccountTiers();

  if (!isOpen) return null;

  const headers: ColumnConfig[] =
    getColumnsForUploadType(uploadType) ??
    (data.length > 0 ? getFallbackHeaders(data[0]) : []);

  return (
    <div className="preview-modal-backdrop">
      <div className="preview-modal-wrapper">
        <div className="preview-modal-content">
          <div className="preview-header">
            <h2 className="preview-title">Preview: {title}</h2>
            <button className="close-btn" onClick={onClose} aria-label="Close">
              ✕
            </button>
          </div>

          <div className="preview-info">
            <p className="record-count">{recordCount}</p>
          </div>

          <div className="preview-table-container">
            <table className="preview-table">
              <thead>
                <tr>
                  {headers.map((col, i) => (
                    <th key={i}>{col.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.length > 0 ? (
                  data.map((record, rowIndex) => (
                    <tr key={rowIndex}>
                      {headers.map((col, colIndex) => {
                        const value = getCellValue(record, col, formatTier);
                        return (
                          <td key={colIndex} title={value}>
                            {truncate(String(value))}
                          </td>
                        );
                      })}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={headers.length || 1}
                      style={{ textAlign: "center", padding: "24px" }}
                    >
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {data.length > 0 && (
            <div className="preview-footer">
              <p className="showing-text">Showing all {data.length} records</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PreviewModal;
